import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import * as XLSX from 'xlsx';
import { Modal, Button } from "react-bootstrap";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

function OrderManager() {
    const [identificacion, setIdentificacion] = useState("");
    const [orden, setOrden] = useState("");
    const [error, setError] = useState("");
    const [resultados, setResultados] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [detalle, setDetalle] = useState(null);

    // Nuevo estado para la búsqueda solo por orden
    const [searchOrden, setSearchOrden] = useState("");
    // Nuevo estado para la búsqueda por ID de pedido
    const [idPedido, setIdPedido] = useState("");
    // Nuevo estado para la búsqueda por fecha (reporte)
    const [reportDate, setReportDate] = useState("");
    const [reportDateFinal, setReportDateFinal] = useState("");
    // Nuevo estado para Log de cambios de estados
    const [logIdOrden, setLogIdOrden] = useState("");
    const [logDate, setLogDate] = useState("");
    const [logHeadersModal, setLogHeadersModal] = useState(false);
    const [selectedHeaders, setSelectedHeaders] = useState(null);
    const [loadingReport, setLoadingReport] = useState(false);

    // Estado para reporte de pendientes
    const [reportPendingDate, setReportPendingDate] = useState("");
    const [reportPendingDateFinal, setReportPendingDateFinal] = useState("");
    const [loadingPendingReport, setLoadingPendingReport] = useState(false);

    // Estado para reporte de intentos de entrega fallidos
    const [reportDeliveryAttemptsDate, setReportDeliveryAttemptsDate] = useState("");
    const [deliveryReportPage, setDeliveryReportPage] = useState(1);
    const [loadingDeliveryAttemptsReport, setLoadingDeliveryAttemptsReport] = useState(false);

    // Estado para informe con resumen general (Base64)
    const [reportSummaryDate, setReportSummaryDate] = useState("");
    const [loadingSummaryReport, setLoadingSummaryReport] = useState(false);

    // Estado para Descarga BD Domicilios (por shippingDateOrder)
    const [bdDateIni, setBdDateIni] = useState("");
    const [bdDateFin, setBdDateFin] = useState("");
    const [bdFormato, setBdFormato] = useState("xlsx");
    const [bdProgreso, setBdProgreso] = useState({ running: false, tramoActual: 0, totalTramos: 0, pagina: 0, registros: 0, tramos: [] });
    const cancelBdRef = useRef(false);
    const bdAbortRef = useRef(null);

    // Estado para Dashboard
    const [dashboardData, setDashboardData] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [dashboardError, setDashboardError] = useState("");

    // Estado para controlar la pestaña activa
    const [activeTab, setActiveTab] = useState("dashboard");

    // Sidebar tab definitions
    const sidebarTabs = [
        { id: "dashboard", icon: "📈", label: "Dashboard" },
        { id: "combined", icon: "🔍", label: "Identificación y Orden" },
        { id: "order", icon: "📋", label: "Solo por Orden" },
        { id: "idpedido", icon: "🔢", label: "Por ID Pedido" },
        { id: "report", icon: "📊", label: "Reportes" },
        { id: "bdReport", icon: "🗄️", label: "Descarga BD Domicilios" },
        { id: "upload", icon: "📤", label: "Cargue Masivo" },
        { id: "bulkStatus", icon: "🔄", label: "Actualizar Estado" },
        { id: "statusLog", icon: "📝", label: "Log de Estados" },
    ];

    const handleTabChange = (tab) => {
        if (statusUpdateProgress.isUploading || bdProgreso.running) return;
        setActiveTab(tab);
        setResultados([]);
        setError("");
        setIdentificacion("");
        setOrden("");
        setSearchOrden("");
        setIdPedido("");
        setReportDate("");
        setReportDateFinal("");
        setReportPendingDate("");
        setReportPendingDateFinal("");
        setLogIdOrden("");
        setLogDate("");
        setCsvData([]);
        setFileName("");
        setReportSummaryDate("");
        setStatusUpdateCsvData([]);
        setStatusUpdateFileName("");
        setStatusUpdateEstado("");
        setStatusUpdateResults([]);
    };

    /* -------------------------------------------------------------
     * Dashboard: Carga automática de datos
     * ------------------------------------------------------------- */

    const fetchDashboardData = async () => {
        setLoadingDashboard(true);
        setDashboardError("");
        try {
            const response = await axios.get(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-9d19154b-130b-4c9d-946a-5e1e2039235b",
                {
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );
            if (response.data) {
                // La API puede retornar los datos envueltos en un array [{ success, ... }]
                let result = response.data;
                if (Array.isArray(result)) {
                    result = result[0];
                }
                if (result && result.success) {
                    setDashboardData(result);
                } else {
                    setDashboardError("No se pudieron obtener los datos del dashboard.");
                }
            }
        } catch (err) {
            console.error("Error cargando dashboard:", err);
            setDashboardError("Error al consultar los datos del dashboard.");
        } finally {
            setLoadingDashboard(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Dashboard: Colores para gráficos */
    const chartColors = [
        '#4A90D9', '#22c55e', '#F5A623', '#ef4444', '#8b5cf6',
        '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
        '#14b8a6', '#e11d48', '#a855f7', '#0ea5e9', '#eab308'
    ];

    /* Dashboard: Opciones comunes de Chart.js para tema oscuro */
    const darkThemeOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: { color: '#a0a3b5', font: { size: 11, family: 'Inter' }, padding: 15, usePointStyle: true }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 12, 41, 0.95)',
                titleColor: '#f0f0f5',
                bodyColor: '#a0a3b5',
                borderColor: 'rgba(255,255,255,0.12)',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12
            }
        },
        scales: {
            x: {
                ticks: { color: '#6b6f82', font: { size: 10 } },
                grid: { color: 'rgba(255,255,255,0.05)' }
            },
            y: {
                ticks: { color: '#6b6f82', font: { size: 10 } },
                grid: { color: 'rgba(255,255,255,0.05)' }
            }
        }
    };

    /* Dashboard: Preparar datos de gráficos */
    const prepareDashboardCharts = () => {
        if (!dashboardData) return null;

        const { fechas, actualizaciones, errores } = dashboardData;
        const sortedFechas = [...(fechas || [])].sort();
        const shortLabels = sortedFechas.map(f => { const d = f.split('-'); return `${d[2]}/${d[1]}`; });

        // KPIs
        const totalActualizaciones = (actualizaciones || []).reduce((s, i) => s + (i.total_actualizaciones || 0), 0);
        const totalErrores = (errores || []).reduce((s, i) => s + (i.total_no_procesados || 0), 0);

        // Line Chart: Actualizaciones por estado y día
        const estadosUnicos = [...new Set((actualizaciones || []).map(i => i.newStatus))];
        const lineDatasets = estadosUnicos.map((estado, idx) => ({
            label: estado,
            data: sortedFechas.map(fecha => {
                const row = (actualizaciones || []).find(i => i.fecha === fecha && i.newStatus === estado);
                return row ? row.total_actualizaciones : 0;
            }),
            borderColor: chartColors[idx % chartColors.length],
            backgroundColor: chartColors[idx % chartColors.length] + '20',
            fill: true,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 6,
            borderWidth: 2
        }));

        // Bar Chart: Errores por día (apilados por tipo)
        const tiposError = [...new Set((errores || []).map(i => i.tipo_error))];
        const barErrorDatasets = tiposError.map((tipo, idx) => ({
            label: tipo,
            data: sortedFechas.map(fecha => {
                const row = (errores || []).find(i => i.fecha === fecha && i.tipo_error === tipo);
                return row ? row.total_no_procesados : 0;
            }),
            backgroundColor: chartColors[(idx + 3) % chartColors.length] + 'CC',
            borderColor: chartColors[(idx + 3) % chartColors.length],
            borderWidth: 1,
            borderRadius: 4
        }));

        // Doughnut: Distribución de estados (total)
        const statusTotals = {};
        (actualizaciones || []).forEach(i => {
            statusTotals[i.newStatus] = (statusTotals[i.newStatus] || 0) + (i.total_actualizaciones || 0);
        });

        // Doughnut: Distribución de errores por tipo (total)
        const errorTotals = {};
        (errores || []).forEach(i => {
            errorTotals[i.tipo_error] = (errorTotals[i.tipo_error] || 0) + (i.total_no_procesados || 0);
        });

        // Top 5 días con más errores
        const erroresPorDia = {};
        (errores || []).forEach(i => {
            erroresPorDia[i.fecha] = (erroresPorDia[i.fecha] || 0) + (i.total_no_procesados || 0);
        });
        const top5Errores = Object.entries(erroresPorDia)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

        return {
            totalActualizaciones,
            totalErrores,
            lineChart: { labels: shortLabels, datasets: lineDatasets },
            barErrorChart: {
                labels: shortLabels,
                datasets: barErrorDatasets
            },
            doughnutStatus: {
                labels: Object.keys(statusTotals),
                datasets: [{
                    data: Object.values(statusTotals),
                    backgroundColor: Object.keys(statusTotals).map((_, i) => chartColors[i % chartColors.length] + 'DD'),
                    borderColor: Object.keys(statusTotals).map((_, i) => chartColors[i % chartColors.length]),
                    borderWidth: 2,
                    hoverOffset: 6
                }]
            },
            doughnutErrors: {
                labels: Object.keys(errorTotals),
                datasets: [{
                    data: Object.values(errorTotals),
                    backgroundColor: Object.keys(errorTotals).map((_, i) => chartColors[(i + 3) % chartColors.length] + 'DD'),
                    borderColor: Object.keys(errorTotals).map((_, i) => chartColors[(i + 3) % chartColors.length]),
                    borderWidth: 2,
                    hoverOffset: 6
                }]
            },
            top5Errores: {
                labels: top5Errores.map(e => { const d = e[0].split('-'); return `${d[2]}/${d[1]}`; }),
                datasets: [{
                    label: 'Errores',
                    data: top5Errores.map(e => e[1]),
                    backgroundColor: '#ef4444CC',
                    borderColor: '#ef4444',
                    borderWidth: 1,
                    borderRadius: 6
                }]
            }
        };
    };

    const dashCharts = prepareDashboardCharts();

    /* -------------------------------------------------------------
     * Utilidades (Parser CSV Manual)
     * ------------------------------------------------------------- */

    const csvToJSON = (str) => {
        const firstLine = str.slice(0, str.indexOf("\n"));
        const delimiter = firstLine.includes(";") ? ";" : ",";

        const headers = firstLine.split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
        const rows = str.slice(str.indexOf("\n") + 1).split("\n");

        const arr = rows.map((row) => {
            if (!row.trim()) return null;

            const values = row.split(delimiter);
            const el = headers.reduce((object, header, index) => {
                let value = values[index] ? values[index].trim() : "";
                if (value.startsWith('"') && value.endsWith('"')) {
                    value = value.substring(1, value.length - 1);
                }
                object[header] = value;
                return object;
            }, {});
            return el;
        });

        return arr.filter(obj => obj && Object.values(obj).some(val => val !== ""));
    };

    // n8n puede responder un arreglo, { data: [...] }, items [{ json }] o un solo objeto
    const normalizarFilas = (respuesta) => {
        let filas = respuesta;
        if (filas && !Array.isArray(filas) && Array.isArray(filas.data)) filas = filas.data;
        if (filas && !Array.isArray(filas) && typeof filas === "object") filas = Object.keys(filas).length > 0 ? [filas] : [];
        if (!Array.isArray(filas)) return [];
        return filas
            .map(f => (f && f.json && typeof f.json === "object" ? f.json : f))
            .filter(f => f && typeof f === "object" && Object.keys(f).length > 0);
    };

    const validarInput = (valor) => {
        return valor && valor.trim().length >= 4;
    };

    /* -------------------------------------------------------------
     * Métodos de Consumo API
     * ------------------------------------------------------------- */

    const fetchCombined = async () => {
        if (!validarInput(identificacion)) {
            setError("La identificación debe tener mínimo 4 caracteres.");
            return;
        }

        setError("");
        const idClean = identificacion.trim();
        const ordenClean = orden.trim();

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-527aa80f-ff76-4aa7-867b-80095fca61a8",
                {
                    identificacion: idClean,
                    orden: ordenClean
                },
                {
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            setResultados(response.data || []);
        } catch (err) {
            console.error(err);
            setError("Error en la consulta al servidor.");
        }
    };

    const fetchByOrder = async () => {
        if (!searchOrden.trim()) {
            setError("Por favor ingrese un número de orden.");
            return;
        }

        setError("");

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-432b228f-1f85-47c5-a8b4-073a1e55a001",
                {
                    orden: searchOrden
                },
                {
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            setResultados(response.data || []);
        } catch (err) {
            console.error(err);
            setError("Error en la consulta por orden.");
        }
    };

    const fetchByIdPedido = async () => {
        if (!idPedido || idPedido.trim() === "") {
            setError("Por favor ingrese un ID de pedido.");
            return;
        }

        if (isNaN(idPedido)) {
            setError("El ID de pedido debe ser numérico.");
            return;
        }

        setError("");

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-7dcae676-8232-4e76-8dcd-4454b8626c4b-Idpedido",
                {
                    idpedido: parseInt(idPedido)
                },
                {
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            setResultados(response.data || []);
        } catch (err) {
            console.error(err);
            setError("Error en la consulta por ID de pedido.");
        }
    };

    const fetchByDate = async () => {
        if (!reportDate || !reportDateFinal) {
            setError("Por favor seleccione ambas fechas (inicio y final).");
            return;
        }

        // Fechas en UTC para que la zona horaria no corra los días
        const toUTC = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
        const toDDMMYYYY = (dt) => `${String(dt.getUTCDate()).padStart(2, '0')}/${String(dt.getUTCMonth() + 1).padStart(2, '0')}/${dt.getUTCFullYear()}`;
        const DAY_MS = 1000 * 60 * 60 * 24;

        const dateStart = toUTC(reportDate);
        const dateEnd = toUTC(reportDateFinal);

        if (dateStart > dateEnd) {
            setError("La fecha de inicio no puede ser mayor a la fecha final.");
            return;
        }

        const totalDays = Math.round((dateEnd - dateStart) / DAY_MS) + 1;

        if (totalDays > 31) {
            setError("El rango máximo de consulta es de 31 días.");
            return;
        }

        setError("");
        setLoadingReport(true);

        // El backend soporta máximo 7 días por consulta: se divide el rango en tramos de 7 días
        const tramos = [];
        for (let ini = dateStart; ini <= dateEnd; ini = new Date(ini.getTime() + 7 * DAY_MS)) {
            const fin = new Date(Math.min(ini.getTime() + 6 * DAY_MS, dateEnd.getTime()));
            tramos.push({ date: toDDMMYYYY(ini), dateFinal: toDDMMYYYY(fin) });
        }

        try {
            let data = [];
            let ultimaRespuesta = null;

            for (let i = 0; i < tramos.length; i++) {
                console.log(`Consultando reporte tramo ${i + 1}/${tramos.length}:`, tramos[i].date, "a", tramos[i].dateFinal);

                const response = await axios.post(
                    "https://helpharmadomicilios.cielum.co/webhook/PRO-7dcae676-8232-4e76-8dcd-4454b8626c4b",
                    tramos[i],
                    {
                        auth: {
                            username: "DOMICILIOSAPI_operador_logistico",
                            password: "DOMICILIOSAPI_operador_logistico"
                        }
                    }
                );

                ultimaRespuesta = response.data;

                // n8n responde { error, mesage } cuando rechaza la consulta: no es un registro del reporte
                const r = Array.isArray(response.data) ? response.data[0] : response.data;
                if (r && typeof r === "object" && r.error && Object.keys(r).length <= 3) {
                    throw new Error(`El servidor rechazó la consulta (${tramos[i].date} a ${tramos[i].dateFinal}): ${r.mesage || r.message || JSON.stringify(r)}`);
                }

                const filas = normalizarFilas(response.data);
                console.log(`Tramo ${i + 1}: ${filas.length} registro(s)`);
                data = data.concat(filas);
            }

            if (data.length > 0) {
                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Reporte");
                XLSX.writeFile(wb, `Reporte_Diario_${reportDate}_a_${reportDateFinal}.xlsx`);
                alert(`✅ Reporte generado y descargado exitosamente (${data.length} registros).`);
            } else {
                console.warn("Respuesta sin datos del servidor:", ultimaRespuesta);
                const muestra = JSON.stringify(ultimaRespuesta);
                setError(`⚠️ No se encontraron datos para el rango seleccionado. Respuesta del servidor: ${muestra ? muestra.slice(0, 300) : "(vacía)"}`);
            }

            // No mostrar datos en pantalla, solo descargar el Excel
        } catch (err) {
            console.error(err);
            const status = err.response?.status;
            setError(status ? `Error en la consulta por rango de fechas (HTTP ${status}).` : err.message);
        } finally {
            setLoadingReport(false);
        }
    };

    const fetchPendingReport = async () => {
        if (!reportPendingDate || !reportPendingDateFinal) {
            setError("Por favor seleccione ambas fechas (inicio y final).");
            return;
        }

        const dateStart = new Date(reportPendingDate);
        const dateEnd = new Date(reportPendingDateFinal);

        if (dateStart > dateEnd) {
            setError("La fecha de inicio no puede ser mayor a la fecha final.");
            return;
        }

        const diffTime = Math.abs(dateEnd - dateStart);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 15) {
            setError("El rango máximo de consulta es de 15 días.");
            return;
        }

        setError("");
        setLoadingPendingReport(true);

        const [year, month, day] = reportPendingDate.split('-');
        const formattedDate = `${day}/${month}/${year}`;

        const [yearF, monthF, dayF] = reportPendingDateFinal.split('-');
        const formattedDateFinal = `${dayF}/${monthF}/${yearF}`;

        console.log("Consultando pendientes para rango:", formattedDate, "a", formattedDateFinal);

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-7dcae676-8232-4e76-8dcd-pedido-pendinete-entregar",
                {
                    date: formattedDate,
                    dateFinal: formattedDateFinal
                },
                {
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            let data = response.data;

            if (data && !Array.isArray(data) && data.data && Array.isArray(data.data)) {
                data = data.data;
            } else if (!Array.isArray(data)) {
                data = [];
            }

            if (data.length > 0) {
                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Pendientes");
                XLSX.writeFile(wb, `Reporte_Pendientes_${reportPendingDate}_a_${reportPendingDateFinal}.xlsx`);
                console.log("Reporte descargado.");
            } else {
                setError("⚠️ No se encontraron datos de pendientes para el rango de fechas seleccionado.");
            }

        } catch (err) {
            console.error(err);
            setError("Error en la consulta de pendientes.");
        } finally {
            setLoadingPendingReport(false);
        }
    };

    const handleSearchPendingReport = async (e) => {
        e.preventDefault();
        await fetchPendingReport();
    };

    const fetchDeliveryAttemptsReport = async () => {
        if (!reportDeliveryAttemptsDate) {
            setError("Por favor seleccione una fecha.");
            return;
        }

        setError("");
        setLoadingDeliveryAttemptsReport(true);

        const [year, month, day] = reportDeliveryAttemptsDate.split('-');
        const formattedDate = `${day}/${month}/${year}`;

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-7dcae676-8232-4e76-intentos-fallidos-entrega",
                {
                    date: formattedDate,
                    page: deliveryReportPage
                },
                {
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            let data = response.data;

            if (data && !Array.isArray(data) && data.data && Array.isArray(data.data)) {
                data = data.data;
            } else if (!Array.isArray(data)) {
                data = [];
            }

            if (data.length > 0) {
                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "IntentosFallidos");
                XLSX.writeFile(wb, `Reporte_Intentos_Fallidos_${reportDeliveryAttemptsDate}.xlsx`);
                console.log("Reporte descargado.");
            } else {
                setError("⚠️ No se encontraron datos de intentos fallidos para la fecha seleccionada.");
            }

        } catch (err) {
            console.error(err);
            setError("Error en la consulta de intentos fallidos.");
        } finally {
            setLoadingDeliveryAttemptsReport(false);
        }
    };

    const handleSearchDeliveryAttemptsReport = async (e) => {
        e.preventDefault();
        await fetchDeliveryAttemptsReport();
    };

    const fetchSummaryReport = async () => {
        if (!reportSummaryDate) {
            setError("Por favor seleccione una fecha.");
            return;
        }

        setError("");
        setLoadingSummaryReport(true);

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-f0bfa95b-5c02-45e3-9179-98926b44f88f",
                {
                    date: reportSummaryDate
                },
                {
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            const data = response.data;

            if (!data || (!data.actualizaciones && !data.noAdmitidos)) {
                setError("⚠️ No se encontraron archivos para la fecha seleccionada.");
                return;
            }

            // Función para descargar un archivo Excel desde Base64
            const descargarExcel = (base64, filename) => {
                const link = document.createElement('a');
                link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            };

            let downloadCount = 0;

            // Descargar archivo de actualizaciones
            if (data.actualizaciones && data.actualizaciones.base64) {
                descargarExcel(data.actualizaciones.base64, data.actualizaciones.filename || `Actualizaciones_${reportSummaryDate}.xlsx`);
                downloadCount++;
            }

            // Descargar archivo de no admitidos
            if (data.noAdmitidos && data.noAdmitidos.base64) {
                descargarExcel(data.noAdmitidos.base64, data.noAdmitidos.filename || `NoAdmitidos_${reportSummaryDate}.xlsx`);
                downloadCount++;
            }

            if (downloadCount > 0) {
                alert(`✅ Descarga completada. Se descargaron ${downloadCount} archivo(s) exitosamente.`);
            } else {
                setError("⚠️ No se pudieron descargar los archivos recibidos.");
            }

        } catch (err) {
            console.error(err);
            setError("Error en la consulta del informe con resumen general.");
        } finally {
            setLoadingSummaryReport(false);
        }
    };

    const handleSearchSummaryReport = async (e) => {
        e.preventDefault();
        await fetchSummaryReport();
    };

    /* -------------------------------------------------------------
     * Descarga BD Domicilios: toda la tabla domicilios filtrada por
     * shippingDateOrder, en tramos de 7 días y páginas de 2.000 filas
     * ------------------------------------------------------------- */

    const BD_REPORT_URL = "https://helpharmadomicilios.cielum.co/webhook/PRO-5c2e8a41-reporte-domicilios-fecha-envio";
    const BD_PAGE_SIZE = 2000;
    const BD_MAX_DIAS = 31;
    const BD_MAX_PAGINAS = 250; // tope de seguridad: 500.000 filas por tramo de 7 días

    const bdToUTC = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
    const bdISO = (dt) => dt.toISOString().slice(0, 10);
    const bdDDMM = (s) => { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

    // Lectura: se puede reintentar sin riesgo ante fallas de red o 5xx
    const bdPostConReintento = async (body, intentos = 3) => {
        for (let i = 1; i <= intentos; i++) {
            if (cancelBdRef.current) throw new axios.CanceledError("cancelado");
            try {
                return await axios.post(BD_REPORT_URL, body, {
                    timeout: 180000,
                    signal: bdAbortRef.current?.signal,
                    auth: {
                        username: "DOMICILIOS@GuararRegistros",
                        password: "DOMICILIOS@GuararRegistros"
                    }
                });
            } catch (err) {
                if (axios.isCancel(err)) throw err;
                const status = err.response?.status;
                if (i === intentos || (status && status < 500)) throw err;
                await new Promise(r => setTimeout(r, 3000 * i));
            }
        }
    };

    const bdDescargarArchivo = (rows, nombreBase) => {
        // Unión de las columnas de todas las filas, por si alguna trae campos adicionales
        const columnas = [...rows.reduce((s, r) => { Object.keys(r).forEach(k => s.add(k)); return s; }, new Set())];
        // Excel no admite más de 1.048.576 filas: en ese caso se fuerza CSV
        const formato = bdFormato === "xlsx" && rows.length < 1048000 ? "xlsx" : "csv";

        if (formato === "xlsx") {
            // Excel limita cada celda a 32.767 caracteres; se modifica en sitio para no duplicar las filas en memoria
            rows.forEach(r => {
                for (const c of columnas) {
                    const v = r[c];
                    if (typeof v === "string" && v.length > 32767) r[c] = v.slice(0, 32767);
                    else if (v !== null && typeof v === "object" && !(v instanceof Date)) r[c] = JSON.stringify(v);
                }
            });
            const ws = XLSX.utils.json_to_sheet(rows, { header: columnas });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Domicilios");
            XLSX.writeFile(wb, `${nombreBase}.xlsx`, { compression: true });
        } else {
            // CSV con ";" y BOM UTF-8 para que Excel en español lo abra con tildes y columnas correctas
            const esc = (v) => {
                if (v === null || v === undefined) return "";
                const s = typeof v === "object" ? JSON.stringify(v) : String(v);
                return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const lineas = [columnas.join(";")];
            rows.forEach(r => lineas.push(columnas.map(c => esc(r[c])).join(";")));
            const blob = new Blob(["﻿" + lineas.join("\r\n")], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${nombreBase}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
        return formato;
    };

    const fetchBdReport = async () => {
        if (!bdDateIni || !bdDateFin) {
            setError("Por favor seleccione ambas fechas (inicio y final).");
            return;
        }

        const ini = bdToUTC(bdDateIni);
        const fin = bdToUTC(bdDateFin);
        const DAY_MS = 86400000;

        if (ini > fin) {
            setError("La fecha de inicio no puede ser mayor a la fecha final.");
            return;
        }
        if (Math.round((fin - ini) / DAY_MS) + 1 > BD_MAX_DIAS) {
            setError(`El rango máximo de consulta es de ${BD_MAX_DIAS} días.`);
            return;
        }

        const tramos = [];
        for (let t = ini; t <= fin; t = new Date(t.getTime() + 7 * DAY_MS)) {
            const tFin = new Date(Math.min(t.getTime() + 6 * DAY_MS, fin.getTime()));
            tramos.push({ date: bdISO(t), dateFinal: bdISO(tFin), registros: 0, estado: "pendiente" });
        }

        setError("");
        cancelBdRef.current = false;
        bdAbortRef.current = new AbortController();
        setBdProgreso({ running: true, tramoActual: 0, totalTramos: tramos.length, pagina: 0, registros: 0, tramos: tramos.map(t => ({ ...t })) });

        const actualizarTramo = (idx, cambios) => {
            tramos[idx] = { ...tramos[idx], ...cambios };
            const copia = tramos.map(t => ({ ...t }));
            setBdProgreso(prev => ({ ...prev, tramos: copia }));
        };

        let todas = [];
        let cancelado = false;

        try {
            for (let i = 0; i < tramos.length && !cancelado; i++) {
                actualizarTramo(i, { estado: "consultando" });
                let page = 1;
                let hasMore = true;
                let lastId = 0;

                while (hasMore) {
                    if (cancelBdRef.current) { cancelado = true; break; }

                    const tramoActual = i + 1;
                    const pagina = page;
                    setBdProgreso(prev => ({ ...prev, tramoActual, pagina }));

                    const response = await bdPostConReintento({
                        date: tramos[i].date,
                        dateFinal: tramos[i].dateFinal,
                        page,
                        pageSize: BD_PAGE_SIZE,
                        lastId
                    });

                    const data = Array.isArray(response.data) ? response.data[0] : response.data;
                    if (!data || data.error || data.ok === false || !Array.isArray(data.rows)) {
                        throw new Error(data?.message || data?.mesage || "Respuesta inesperada del servidor");
                    }
                    // Defensa: si el backend ignora page/pageSize se aborta en vez de duplicar o truncar datos
                    if (data.page !== undefined && Number(data.page) !== page) {
                        throw new Error(`El servidor devolvió el bloque ${data.page} cuando se pidió el ${page}`);
                    }
                    if (data.pageSize !== undefined && Number(data.pageSize) !== BD_PAGE_SIZE) {
                        throw new Error(`El servidor aplicó un tamaño de bloque de ${data.pageSize} (se esperaba ${BD_PAGE_SIZE})`);
                    }

                    // Defensa: los id deben venir estrictamente crecientes y mayores al último bloque; si no, hay filas repetidas
                    let idPrevio = lastId;
                    for (const r of data.rows) {
                        const id = Number(r.id);
                        if (!(id > idPrevio)) {
                            throw new Error(`El servidor devolvió registros repetidos o desordenados (id ${r.id}); descarga detenida para no generar datos duplicados`);
                        }
                        idPrevio = id;
                    }
                    lastId = idPrevio;

                    for (const r of data.rows) todas.push(r);
                    actualizarTramo(i, { registros: tramos[i].registros + data.rows.length });
                    const registros = todas.length;
                    setBdProgreso(prev => ({ ...prev, registros }));

                    hasMore = data.rows.length === BD_PAGE_SIZE && data.hasMore !== false;
                    page++;
                    if (hasMore && page > BD_MAX_PAGINAS) {
                        throw new Error(`Se superó el máximo de ${BD_MAX_PAGINAS} bloques en el tramo ${bdDDMM(tramos[i].date)} – ${bdDDMM(tramos[i].dateFinal)}`);
                    }
                }

                actualizarTramo(i, { estado: cancelado ? "cancelado" : "completado" });
            }

            if (cancelado) {
                setError(`Descarga detenida por el usuario. No se generó archivo (${todas.length.toLocaleString()} registros consultados).`);
            } else if (todas.length === 0) {
                setError("⚠️ No hay registros con fecha de envío (shippingDateOrder) en el rango seleccionado.");
            } else {
                const nombreBase = `Domicilios_FechaEnvio_${bdDateIni}_a_${bdDateFin}`;
                const formato = bdDescargarArchivo(todas, nombreBase);
                alert(`✅ Descarga completada.\n\nRegistros: ${todas.length.toLocaleString()}\nArchivo: ${nombreBase}.${formato}`);
            }
        } catch (err) {
            if (axios.isCancel(err) || cancelBdRef.current) {
                const idx = tramos.findIndex(t => t.estado === "consultando");
                if (idx >= 0) actualizarTramo(idx, { estado: "cancelado" });
                setError(`Descarga detenida por el usuario. No se generó archivo (${todas.length.toLocaleString()} registros consultados).`);
                return;
            }
            console.error("Error en descarga BD domicilios:", err);
            const tramoFallido = tramos.findIndex(t => t.estado === "consultando");
            if (tramoFallido >= 0) actualizarTramo(tramoFallido, { estado: "error" });
            const status = err.response?.status;
            const errData = Array.isArray(err.response?.data) ? err.response.data[0] : err.response?.data;
            const detalle = errData?.message || errData?.mesage
                || (!err.response
                    ? (err.code === "ECONNABORTED"
                        ? "el servidor tardó demasiado en responder; intente con un rango menor"
                        : err.message === "Network Error"
                            ? "no se pudo conectar con el servicio de reportes (verifique su conexión o que el servicio esté activo)"
                            : err.message)
                    : err.message);
            setError(`Error en la descarga${status ? ` (HTTP ${status})` : ""}: ${detalle}. No se generó archivo.`);
        } finally {
            setBdProgreso(prev => ({ ...prev, running: false }));
            cancelBdRef.current = false;
            bdAbortRef.current = null;
        }
    };

    const handleBdReport = async (e) => {
        e.preventDefault();
        await fetchBdReport();
    };

    const fetchStatusLog = async () => {
        if (!logIdOrden && !logDate) {
            setError("Debe proporcionar al menos un criterio: ID Orden o Fecha.");
            return;
        }

        setError("");

        const payload = {};
        if (logIdOrden) payload.idorden = logIdOrden;
        if (logDate) payload.date = logDate;

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-7dcae676-8232-4e76-8dcd-actualizaciones-estados",
                payload,
                {
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            console.log("Respuesta Status Log:", response.data);
            setResultados(response.data || []);
        } catch (err) {
            console.error(err);
            setError("Error en la consulta de log de estados.");
        }
    };

    /* -------------------------------------------------------------
     * Handlers de Formularios
     * ------------------------------------------------------------- */

    const handleSubmit = async (e) => {
        e.preventDefault();
        await fetchCombined();
    };

    const handleSearchByOrder = async (e) => {
        e.preventDefault();
        await fetchByOrder();
    };

    const handleSearchByIdPedido = async (e) => {
        e.preventDefault();
        await fetchByIdPedido();
    };

    const handleSearchByDate = async (e) => {
        e.preventDefault();
        await fetchByDate();
    };

    const handleSearchStatusLog = async (e) => {
        e.preventDefault();
        await fetchStatusLog();
    };

    const refreshData = async () => {
        if (activeTab === "combined") {
            await fetchCombined();
        } else if (activeTab === "order") {
            await fetchByOrder();
        } else if (activeTab === "idpedido") {
            await fetchByIdPedido();
        } else if (activeTab === "report") {
            await fetchByDate();
        } else if (activeTab === "statusLog") {
            await fetchStatusLog();
        }
    };

    const handleShowDetails = (item) => {
        let payloadParsed = null;
        try {
            payloadParsed = JSON.parse(item.payload);
        } catch (e) {
            console.error("Error al parsear payload:", e);
        }

        setDetalle({
            ...item,
            payloadParsed,
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setDetalle(null);
    };

    const handleShowHeaders = (headersStr) => {
        try {
            const parsed = JSON.parse(headersStr);
            setSelectedHeaders(parsed);
        } catch (e) {
            setSelectedHeaders({ raw: headersStr, error: "Invalid JSON" });
        }
        setLogHeadersModal(true);
    };

    const handleCloseHeadersModal = () => {
        setLogHeadersModal(false);
        setSelectedHeaders(null);
    };

    const handleEntregado = async (item) => {
        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-aba8e5ec-ad9e-4fd8-a01f-442bfafa4a8f",
                {
                    identificationPatient: item.identificationPatient,
                    identificationTypePatient: item.identificationTypePatient,
                    authorizationNumber: item.authorizationNumber,
                    id: item.id,
                    estado: 4
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    auth: {
                        username: "DOMICILIOS@GuararRegistros",
                        password: "DOMICILIOS@GuararRegistros"
                    }
                }
            );

            alert("Entrega confirmada correctamente ✅");
            await refreshData();
        } catch (error) {
            console.error("Error en Entregado:", error);
            alert("Error al confirmar entrega ❌");
        }
    };

    const handleDeclinar = async (item) => {
        const confirmDecline = window.confirm(
            `¿Está seguro que desea marcar como DEVOLUCIÓN el pedido:\n\n` +
            `Orden: ${item.authorizationNumber}\n` +
            `Paciente: ${item.namePatient}`
        );

        if (!confirmDecline) return;

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/QA-aba8e5ec-ad9e-4fd8-a01f-442bfafa4a8f",
                {
                    identificationPatient: item.identificationPatient,
                    identificationTypePatient: item.identificationTypePatient,
                    authorizationNumber: item.authorizationNumber,
                    id: item.id,
                    estado: 6
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    auth: {
                        username: "DOMICILIOS@GuararRegistros",
                        password: "DOMICILIOS@GuararRegistros"
                    }
                }
            );

            console.log("Respuesta declinar:", response.data);
            alert("Entrega declinada correctamente ⚠️");
            await refreshData();
        } catch (error) {
            console.error("Error en Declinar:", error);
            alert("Error al declinar entrega ❌");
        }
    };

    const allItemsReadyForBulkAction = () => {
        if (resultados.length === 0) return false;
        return resultados.every(item =>
            (item.statusOrderHeadquarters === "DELIVERED" || item.statusOrderHeadquarters === "RETURNED") &&
            item.statusOrder === "CHECKED_OUT" &&
            item.status === 1
        );
    };

    const handleBulkAction = async () => {
        const confirmAction = window.confirm(
            `¿Está seguro que desea ejecutar la acción masiva?\n\n` +
            `Se procesarán ${resultados.length} pedido(s).`
        );

        if (!confirmAction) return;

        const payload = resultados.map(item => ({
            idorden: item.idorden,
            authorizationNumber: item.authorizationNumber
        }));

        try {
            const response = await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/PRO-3d77851d-5885-4f5e-815d-e2994df08ad3",
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            console.log("Respuesta acción masiva:", response.data);
            alert(`Respuesta del servidor:\n${JSON.stringify(response.data, null, 2)}`);
            await refreshData();
        } catch (error) {
            console.error("Error en acción masiva:", error);
            alert("Error al ejecutar acción masiva ❌");
        }
    };

    /* -------------------------------------------------------------
     * Carga Masiva (CSV)
     * ------------------------------------------------------------- */
    const [csvData, setCsvData] = useState([]);
    const [fileName, setFileName] = useState("");
    const [uploadProgress, setUploadProgress] = useState({ isUploading: false, current: 0, total: 0, skipped: 0 });
    const [cancelUpload, setCancelUpload] = useState(false);

    /* -------------------------------------------------------------
     * Actualización Masiva de Estados (Excel: solo idOrden)
     * ------------------------------------------------------------- */
    const [statusUpdateCsvData, setStatusUpdateCsvData] = useState([]);
    const [statusUpdateFileName, setStatusUpdateFileName] = useState("");
    const [statusUpdateEstado, setStatusUpdateEstado] = useState("");
    const [statusUpdateProgress, setStatusUpdateProgress] = useState({ isUploading: false, current: 0, total: 0, success: 0, failed: 0 });
    const [statusUpdateResults, setStatusUpdateResults] = useState([]);
    const cancelStatusUpdateRef = useRef(false);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setFileName(file.name);
        const reader = new FileReader();

        reader.onload = (evt) => {
            const text = evt.target.result;
            const parsedData = csvToJSON(text);
            console.log("Datos CSV parseados:", parsedData);
            setCsvData(parsedData);
        };

        reader.readAsText(file);
    };

    const validateRecord = (record) => {
        const idOrder = record.idOrder || record.idorder || "";
        if (!idOrder || isNaN(idOrder) || idOrder.trim() === "") return false;

        const authNumber = record.authorizationNumber || "";
        if (!authNumber || authNumber.trim() === "") return false;

        const statusOrder = record.statusOrder || "";
        if (!statusOrder || statusOrder.trim() === "") return false;

        return true;
    };

    const handleSendUpload = async () => {
        if (csvData.length === 0) {
            alert("No hay datos para enviar. Por favor cargue un archivo CSV válido.");
            return;
        }

        const validRecords = csvData.filter(validateRecord);
        const skippedCount = csvData.length - validRecords.length;

        if (validRecords.length === 0) {
            alert(`❌ No hay registros válidos para enviar. Todos los ${csvData.length} registros fueron omitidos por no cumplir con los requisitos mínimos.`);
            return;
        }

        if (skippedCount > 0) {
            const confirmSend = window.confirm(
                `⚠️ Se omitirán ${skippedCount} registro(s) que no cumplen con los requisitos mínimos.\n\n` +
                `Registros válidos: ${validRecords.length}\n` +
                `Registros omitidos: ${skippedCount}\n\n` +
                `¿Desea continuar con el envío?`
            );
            if (!confirmSend) return;
        }

        const CHUNK_SIZE = 100;
        const totalChunks = Math.ceil(validRecords.length / CHUNK_SIZE);

        setCancelUpload(false);
        setUploadProgress({ isUploading: true, current: 0, total: totalChunks, skipped: skippedCount });

        try {
            for (let i = 0; i < totalChunks; i++) {
                if (cancelUpload) {
                    alert(`⚠️ Carga cancelada. Se enviaron ${i} de ${totalChunks} lote(s) (${i * CHUNK_SIZE} registros)`);
                    break;
                }

                const start = i * CHUNK_SIZE;
                const end = start + CHUNK_SIZE;
                const chunk = validRecords.slice(start, end);

                console.log(`Enviando lote ${i + 1}/${totalChunks} (${chunk.length} registros)`);

                const response = await axios.post(
                    "https://helpharmadomicilios.cielum.co/webhook/PRO-e95f2874-2612-4457-b10b-2331decff166", // en produccion se envian los datos
                    chunk,
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                        auth: {
                            username: "DOMICILIOS@GuararRegistros",
                            password: "DOMICILIOS@GuararRegistros"
                        }
                    }
                );

                //console.log(`Respuesta lote ${i + 1}:`, response.data);
                setUploadProgress({ isUploading: true, current: i + 1, total: totalChunks, skipped: skippedCount });

                if (i < totalChunks - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1500));
                }
            }

            if (!cancelUpload) {
                let message = `✅ Carga masiva completada:\n\n`;
                message += `• Registros enviados: ${validRecords.length}\n`;
                if (skippedCount > 0) {
                    message += `• Registros omitidos: ${skippedCount} (no cumplen requisitos)\n`;
                }
                message += `• Lotes procesados: ${totalChunks}`;
                alert(message);
            }

            setCsvData([]);
            setFileName("");
            setUploadProgress({ isUploading: false, current: 0, total: 0, skipped: 0 });
            setCancelUpload(false);
        } catch (error) {
            console.error("Error en Carga Masiva:", error);
            alert("❌ Error al enviar carga masiva. Revise la consola para más detalles.");
            setUploadProgress({ isUploading: false, current: 0, total: 0, skipped: 0 });
            setCancelUpload(false);
        }
    };

    const handleStopUpload = () => {
        setCancelUpload(true);
    };

    /* -------------------------------------------------------------
     * Actualización Masiva de Estados (Excel: solo idOrden)
     * ------------------------------------------------------------- */

    const handleStatusUpdateFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setStatusUpdateFileName(file.name);
        const reader = new FileReader();

        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
                setStatusUpdateCsvData(jsonData);
            } catch (err) {
                console.error("Error al leer el archivo Excel:", err);
                alert("❌ Error al leer el archivo Excel. Verifique que el formato sea correcto (.xlsx).");
                setStatusUpdateCsvData([]);
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const normalizeIdOrden = (record) => {
        const idOrden = record.idOrden ?? record.IdOrden ?? record.Idorden ?? record.idorden ?? record.IDORDEN ?? "";
        return String(idOrden).trim();
    };

    const validateStatusUpdateRecord = (record) => normalizeIdOrden(record) !== "";

    const handleSendStatusUpdate = async () => {
        if (statusUpdateCsvData.length === 0) {
            alert("No hay datos para enviar. Por favor cargue un archivo Excel válido.");
            return;
        }

        if (!statusUpdateEstado || statusUpdateEstado.trim() === "") {
            setError("Debe indicar el estado que desea aplicar a todos los registros.");
            return;
        }

        const estadoAplicar = statusUpdateEstado.trim();

        const validRecords = statusUpdateCsvData
            .filter(validateStatusUpdateRecord)
            .map((r) => ({ idOrden: normalizeIdOrden(r), estado: estadoAplicar, manual: 1 }));

        const skippedCount = statusUpdateCsvData.length - validRecords.length;

        if (validRecords.length === 0) {
            alert(`❌ No hay registros válidos para enviar. Todos los ${statusUpdateCsvData.length} registros fueron omitidos por no contener idOrden.`);
            return;
        }

        if (skippedCount > 0) {
            const confirmSend = window.confirm(
                `⚠️ Se omitirán ${skippedCount} registro(s) sin idOrden válido.\n\n` +
                `Registros válidos: ${validRecords.length}\n` +
                `Registros omitidos: ${skippedCount}\n\n` +
                `¿Desea continuar con el envío?`
            );
            if (!confirmSend) return;
        }

        setError("");
        cancelStatusUpdateRef.current = false;
        setStatusUpdateResults([]);
        setStatusUpdateProgress({ isUploading: true, current: 0, total: validRecords.length, success: 0, failed: 0 });

        let successCount = 0;
        let failedCount = 0;

        for (let i = 0; i < validRecords.length; i++) {
            if (cancelStatusUpdateRef.current) {
                setStatusUpdateResults(prev => [...prev, {
                    idOrden: "-",
                    estado: "-",
                    success: null,
                    message: `Proceso cancelado. Se procesaron ${i} de ${validRecords.length} registro(s).`
                }]);
                break;
            }

            const record = validRecords[i];

            try {
                const response = await axios.post(
                    "https://helpharmadomicilios.cielum.co/webhook/PRO-7fcd5ac0-bf2c-4f4d-9e57-a83b07b6ed76",
                    record,
                    {
                        headers: { "Content-Type": "application/json" },
                        auth: {
                            username: "DOMICILIOS@GuararRegistros",
                            password: "DOMICILIOS@GuararRegistros"
                        }
                    }
                );

                successCount++;
                setStatusUpdateResults(prev => [...prev, {
                    idOrden: record.idOrden,
                    estado: record.estado,
                    success: true,
                    message: typeof response.data === "string" ? response.data : JSON.stringify(response.data ?? "OK")
                }]);
            } catch (err) {
                failedCount++;
                const message = err.response?.data?.message || err.message || "Error desconocido";
                setStatusUpdateResults(prev => [...prev, {
                    idOrden: record.idOrden,
                    estado: record.estado,
                    success: false,
                    message
                }]);
            }

            setStatusUpdateProgress({ isUploading: true, current: i + 1, total: validRecords.length, success: successCount, failed: failedCount });

            if (i < validRecords.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
            }
        }

        setStatusUpdateProgress(prev => ({ ...prev, isUploading: false }));

        if (!cancelStatusUpdateRef.current) {
            alert(`✅ Proceso finalizado.\n\nExitosos: ${successCount}\nFallidos: ${failedCount}\nTotal procesados: ${successCount + failedCount}`);
        }

        cancelStatusUpdateRef.current = false;
    };

    const handleStopStatusUpdate = () => {
        cancelStatusUpdateRef.current = true;
    };

    /* -------------------------------------------------------------
     * Helper Functions for Calculated Columns
     * ------------------------------------------------------------- */

    const calcularCumpleEntrega = (shippingDateOrder, dateNotificacion, statusOrder, statusOrderHeadquarters) => {
        if (!shippingDateOrder) return { text: "N/A", color: "gray" };

        const isDelivered = statusOrder === "DELIVERED" && statusOrderHeadquarters === "DELIVERED";

        let shippingDate;
        if (shippingDateOrder.includes('/')) {
            const [day, month, year] = shippingDateOrder.split('/');
            shippingDate = new Date(year, month - 1, day);
        } else {
            shippingDate = new Date(shippingDateOrder);
        }

        let notificationDate;
        if (dateNotificacion && dateNotificacion !== "N/A" && dateNotificacion.trim() !== "") {
            if (dateNotificacion.includes('/')) {
                const [day, month, year] = dateNotificacion.split('/');
                notificationDate = new Date(year, month - 1, day);
            } else {
                notificationDate = new Date(dateNotificacion);
            }
        }

        if (isDelivered) {
            if (!notificationDate || isNaN(notificationDate.getTime())) {
                return { text: "ENTREGADO (Sin notificar)", color: "#22c55e" };
            }

            if (isNaN(shippingDate.getTime())) {
                return { text: "ENTREGADO (Fecha envío inválida)", color: "gray" };
            }

            const sDate = new Date(shippingDate.getFullYear(), shippingDate.getMonth(), shippingDate.getDate());
            const nDate = new Date(notificationDate.getFullYear(), notificationDate.getMonth(), notificationDate.getDate());

            const diffTime = nDate - sDate;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays > 0) {
                return { text: `ENTREGADO (${diffDays} días de retraso)`, color: "#ef4444" };
            } else {
                return { text: "ENTREGADO (A tiempo)", color: "#22c55e" };
            }
        }

        if (!dateNotificacion || dateNotificacion === "N/A" || dateNotificacion.trim() === "") {
            return { text: "Sin Entregar", color: "#f59e0b" };
        } else if (dateNotificacion === "DELIVERED") {
            return { text: "ENTREGADO", color: "#22c55e" };
        }

        if (!notificationDate || isNaN(notificationDate.getTime()) || isNaN(shippingDate.getTime())) {
            return { text: "Fecha inválida", color: "gray" };
        }

        if (notificationDate > shippingDate) {
            return { text: "NO CUMPLIÓ", color: "#ef4444" };
        } else {
            return { text: "CUMPLIÓ", color: "#22c55e" };
        }
    };

    const calcularTiempoEntrega = (packingDate, shippingDateOrder, statusOrder) => {
        if (!packingDate || !shippingDateOrder) return { text: "N/A", color: "gray" };

        let packing, shipping;

        if (packingDate.includes('/')) {
            const [day, month, year] = packingDate.split('/');
            packing = new Date(year, month - 1, day);
        } else {
            packing = new Date(packingDate);
        }

        if (shippingDateOrder.includes('/')) {
            const [day, month, year] = shippingDateOrder.split('/');
            shipping = new Date(year, month - 1, day);
        } else {
            shipping = new Date(shippingDateOrder);
        }

        if (isNaN(packing.getTime()) || isNaN(shipping.getTime())) {
            return { text: "Fecha inválida", color: "gray" };
        }

        const diffTime = Math.abs(packing - shipping);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        let color = "gray";
        if (statusOrder === "CHECKED_OUT") {
            color = "#f59e0b";
        } else if (statusOrder === "DELIVERED") {
            color = "#22c55e";
        } else if (statusOrder === "CHECKED_IN") {
            color = "#22c55e";
        }

        return { text: `${diffDays} día${diffDays !== 1 ? 's' : ''}`, color };
    };

    /* ------------------------------------------------------------- */

    // Get active tab label
    const activeTabInfo = sidebarTabs.find(t => t.id === activeTab);

    return (
        <div className="app-layout">
            {/* Sidebar */}
            <aside className="sidebar">
                <div style={{ padding: '0 0.25rem', marginBottom: '0.75rem' }}>
                    <p style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', margin: 0 }}>
                        Módulos
                    </p>
                </div>
                {sidebarTabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`sidebar-item ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                        disabled={statusUpdateProgress.isUploading || bdProgreso.running}
                    >
                        <span className="sidebar-icon">{tab.icon}</span>
                        <span className="sidebar-label">{tab.label}</span>
                    </button>
                ))}
            </aside>

            {/* Main Content */}
            <main className="main-content">
                {/* Mobile tabs (hidden on desktop via sidebar CSS) */}
                <div className="d-lg-none mb-3">
                    <ul className="nav nav-tabs">
                        {sidebarTabs.map((tab) => (
                            <li className="nav-item" key={tab.id}>
                                <button
                                    className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                                    onClick={() => handleTabChange(tab.id)}
                                    disabled={statusUpdateProgress.isUploading || bdProgreso.running}
                                >
                                    <span style={{ marginRight: '0.3rem' }}>{tab.icon}</span>
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Page Header */}
                <div className="page-header">
                    <h1 className="page-title">{activeTabInfo?.icon} {activeTabInfo?.label || 'Consulta'}</h1>
                    <p className="page-subtitle">Panel de administración de domicilios</p>
                </div>

                {/* Tab Content */}
                <div className="fade-in" key={activeTab}>

                    {/* ===== DASHBOARD ===== */}
                    {activeTab === "dashboard" && (
                        <div>
                            {loadingDashboard && (
                                <div className="glass-card" style={{ padding: '3rem', textAlign: 'center' }}>
                                    <span className="spinner-premium" style={{ marginRight: '0.75rem' }}></span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: 500 }}>Cargando datos del dashboard...</span>
                                </div>
                            )}

                            {dashboardError && (
                                <div className="alert alert-danger">{dashboardError}</div>
                            )}

                            {dashCharts && !loadingDashboard && (
                                <>
                                    {/* KPI Cards */}
                                    <div className="dashboard-kpi-row">
                                        <div className="kpi-card kpi-blue">
                                            <div className="kpi-icon">📊</div>
                                            <div className="kpi-value">{dashCharts.totalActualizaciones.toLocaleString()}</div>
                                            <div className="kpi-label">Total Actualizaciones</div>
                                        </div>
                                        <div className="kpi-card kpi-red">
                                            <div className="kpi-icon">⚠️</div>
                                            <div className="kpi-value">{dashCharts.totalErrores.toLocaleString()}</div>
                                            <div className="kpi-label">Total Errores</div>
                                        </div>
                                        <div className="kpi-card kpi-green">
                                            <div className="kpi-icon">📅</div>
                                            <div className="kpi-value">{dashboardData.diasConsultados}</div>
                                            <div className="kpi-label">Días Consultados</div>
                                        </div>
                                        <div className="kpi-card kpi-amber">
                                            <div className="kpi-icon">🕐</div>
                                            <div className="kpi-value" style={{ fontSize: '1rem' }}>
                                                {(() => {
                                                    try {
                                                        const raw = dashboardData.generadoEn;
                                                        if (!raw) return "N/A";
                                                        // Intentar formatear si es un string de fecha
                                                        // La muestra es "15/3/2026, 1:11:47 p. m."
                                                        return raw.replace(', ', ' | ').replace('p. m.', 'PM').replace('a. m.', 'AM');
                                                    } catch (e) {
                                                        return dashboardData.generadoEn;
                                                    }
                                                })()}
                                            </div>
                                            <div className="kpi-label">Última Actualización (Bogotá)</div>
                                        </div>
                                    </div>

                                    {/* Line Chart: Tendencia de actualizaciones */}
                                    <div className="glass-card chart-container" style={{ marginTop: '1.25rem' }}>
                                        <h6 style={{ fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
                                            📈 Tendencia de Actualizaciones por Estado (últimos {dashboardData.diasConsultados} días)
                                        </h6>
                                        <div style={{ height: '350px' }}>
                                            <Line
                                                data={dashCharts.lineChart}
                                                options={{
                                                    ...darkThemeOptions,
                                                    plugins: {
                                                        ...darkThemeOptions.plugins,
                                                        title: { display: false }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Bar Chart: Errores por día */}
                                    <div className="glass-card chart-container" style={{ marginTop: '1.25rem' }}>
                                        <h6 style={{ fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
                                            📊 Errores Diarios por Tipo
                                        </h6>
                                        <div style={{ height: '300px' }}>
                                            <Bar
                                                data={dashCharts.barErrorChart}
                                                options={{
                                                    ...darkThemeOptions,
                                                    plugins: {
                                                        ...darkThemeOptions.plugins,
                                                        title: { display: false }
                                                    },
                                                    scales: {
                                                        ...darkThemeOptions.scales,
                                                        x: { ...darkThemeOptions.scales.x, stacked: true },
                                                        y: { ...darkThemeOptions.scales.y, stacked: true }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Doughnuts Row */}
                                    <div className="dashboard-grid" style={{ marginTop: '1.25rem' }}>
                                        <div className="glass-card chart-container">
                                            <h6 style={{ fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
                                                🎯 Distribución por Estado
                                            </h6>
                                            <div style={{ height: '280px', display: 'flex', justifyContent: 'center' }}>
                                                <Doughnut
                                                    data={dashCharts.doughnutStatus}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: {
                                                            ...darkThemeOptions.plugins,
                                                            legend: {
                                                                ...darkThemeOptions.plugins.legend,
                                                                position: 'bottom'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="glass-card chart-container">
                                            <h6 style={{ fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
                                                🔴 Distribución de Errores por Tipo
                                            </h6>
                                            <div style={{ height: '280px', display: 'flex', justifyContent: 'center' }}>
                                                <Doughnut
                                                    data={dashCharts.doughnutErrors}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: {
                                                            ...darkThemeOptions.plugins,
                                                            legend: {
                                                                ...darkThemeOptions.plugins.legend,
                                                                position: 'bottom'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Top 5 Días con más errores */}
                                    <div className="glass-card chart-container" style={{ marginTop: '1.25rem' }}>
                                        <h6 style={{ fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
                                            🔥 Top 5 Días con Más Errores
                                        </h6>
                                        <div style={{ height: '250px' }}>
                                            <Bar
                                                data={dashCharts.top5Errores}
                                                options={{
                                                    ...darkThemeOptions,
                                                    indexAxis: 'y',
                                                    plugins: {
                                                        ...darkThemeOptions.plugins,
                                                        legend: { display: false }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Refresh button */}
                                    <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                                        <button className="btn btn-glass" onClick={fetchDashboardData} disabled={loadingDashboard}>
                                            🔄 Actualizar Dashboard
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === "combined" && (
                        <div className="glass-card" style={{ padding: '1.5rem' }}>
                            <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>Búsqueda Combinada</h5>
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-5">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ingrese identificación..."
                                            value={identificacion}
                                            onChange={(e) => setIdentificacion(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ingrese número de orden..."
                                            value={orden}
                                            onChange={(e) => setOrden(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <button className="btn btn-premium w-100" type="submit">
                                            Consultar
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}

                    {activeTab === "order" && (
                        <div className="glass-card" style={{ padding: '1.5rem' }}>
                            <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>Búsqueda por Orden</h5>
                            <form onSubmit={handleSearchByOrder}>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ingrese número de orden para consultar..."
                                        value={searchOrden}
                                        onChange={(e) => setSearchOrden(e.target.value)}
                                        required
                                    />
                                    <button className="btn btn-premium" type="submit">
                                        Consultar por Orden
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {activeTab === "idpedido" && (
                        <div className="glass-card" style={{ padding: '1.5rem' }}>
                            <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>Búsqueda por ID Pedido</h5>
                            <form onSubmit={handleSearchByIdPedido}>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Ingrese ID del pedido (numérico)..."
                                        value={idPedido}
                                        onChange={(e) => setIdPedido(e.target.value)}
                                        required
                                    />
                                    <button className="btn btn-premium" type="submit">
                                        Consultar por ID
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {activeTab === "statusLog" && (
                        <>
                            <div className="glass-card" style={{ padding: '1.5rem' }}>
                                <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>Log de Cambios de Estados</h5>
                                <form onSubmit={handleSearchStatusLog}>
                                    <div className="row g-3">
                                        <div className="col-md-5">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="ID Orden"
                                                value={logIdOrden}
                                                onChange={(e) => setLogIdOrden(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-5">
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={logDate}
                                                onChange={(e) => setLogDate(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <button className="btn btn-premium w-100" type="submit">
                                                Consultar
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="glass-card" style={{ padding: '1.5rem', marginTop: '1rem' }}>
                                <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>Consulta de Log Fallidos de Entrega</h5>
                                <form onSubmit={handleSearchDeliveryAttemptsReport}>
                                    <div className="input-group">
                                        <span className="input-group-text">Fecha</span>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={reportDeliveryAttemptsDate}
                                            onChange={(e) => setReportDeliveryAttemptsDate(e.target.value)}
                                            required
                                        />
                                        <span className="input-group-text">Lote</span>
                                        <select
                                            className="form-select"
                                            value={deliveryReportPage}
                                            onChange={(e) => setDeliveryReportPage(Number(e.target.value))}
                                            style={{ maxWidth: "200px" }}
                                        >
                                            <option value={1}>1 (1 - 25.000)</option>
                                            <option value={2}>2 (25.001 - 50.000)</option>
                                            <option value={3}>3 (50.001 - 75.000)</option>
                                            <option value={4}>4 (75.001 - 100.000)</option>
                                            <option value={5}>5 (100.001 - 125.000)</option>
                                        </select>
                                        <button
                                            className="btn btn-accent"
                                            type="submit"
                                            disabled={loadingDeliveryAttemptsReport}
                                        >
                                            {loadingDeliveryAttemptsReport
                                                ? <><span className="spinner-premium"></span>Generando...</>
                                                : "Consultar y Descargar"
                                            }
                                        </button>
                                    </div>
                                    <small className="text-muted mt-2 d-block fst-italic">
                                        Consultar intentos de entrega fallidos. El reporte tiene un límite de 25.000 registros por lote.
                                        Si necesita más, seleccione el siguiente lote.
                                    </small>
                                </form>
                                {loadingDeliveryAttemptsReport && (
                                    <div className="alert alert-warning mt-3">
                                        <span className="spinner-premium"></span> Generando archivo Excel de Intentos de Entrega, por favor espere...
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === "upload" && (
                        <div className="glass-card" style={{ padding: '1.5rem' }}>
                            <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>📤 Carga Masiva de Pedidos (CSV)</h5>
                            <div className="mb-3">
                                <input
                                    type="file"
                                    className="form-control"
                                    accept=".csv"
                                    onChange={handleFileUpload}
                                    disabled={uploadProgress.isUploading}
                                />
                                {fileName && <small className="text-muted mt-1 d-block">Archivo seleccionado: {fileName}</small>}
                            </div>

                            {csvData.length > 0 && !uploadProgress.isUploading && (
                                <div className="mb-3">
                                    <div className="alert alert-info">
                                        <strong>{csvData.length}</strong> registros encontrados.
                                        <br />
                                        <small>Validando requisitos mínimos antes de enviar...</small>
                                    </div>
                                    <div className="table-premium-wrapper" style={{ maxHeight: "200px", overflow: "auto" }}>
                                        <table className="table table-sm table-bordered" style={{ fontSize: "0.8rem" }}>
                                            <thead>
                                                <tr>
                                                    {Object.keys(csvData[0] || {}).map(k => <th key={k}>{k}</th>)}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {csvData.slice(0, 5).map((row, i) => (
                                                    <tr key={i}>
                                                        {Object.values(row).map((val, j) => <td key={j}>{val}</td>)}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {csvData.length > 5 && <p className="text-center text-muted mt-2" style={{ fontSize: '0.8rem' }}>... y {csvData.length - 5} más</p>}
                                </div>
                            )}

                            {uploadProgress.isUploading && (
                                <div className="mb-3">
                                    <div className="alert alert-warning">
                                        <strong><span className="spinner-premium"></span> Enviando datos...</strong>
                                        <div className="mt-2">
                                            Lote {uploadProgress.current} de {uploadProgress.total}
                                            {uploadProgress.skipped > 0 && (
                                                <span className="text-muted ms-2">
                                                    ({uploadProgress.skipped} omitido(s))
                                                </span>
                                            )}
                                        </div>
                                        <div className="progress mt-2" style={{ height: "25px" }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                role="progressbar"
                                                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                                            >
                                                {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-success-premium"
                                    onClick={handleSendUpload}
                                    disabled={csvData.length === 0 || uploadProgress.isUploading}
                                >
                                    {uploadProgress.isUploading
                                        ? <><span className="spinner-premium"></span>Enviando...</>
                                        : "📤 Enviar al Servidor"
                                    }
                                </button>

                                {uploadProgress.isUploading && (
                                    <button
                                        className="btn btn-danger-premium"
                                        onClick={handleStopUpload}
                                    >
                                        ⏹ Detener
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === "bulkStatus" && (
                        <div className="glass-card" style={{ padding: '1.5rem' }}>
                            <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>🔄 Actualización Masiva de Estados</h5>
                            <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                Cargue un archivo Excel (.xlsx) con una única columna <strong>idOrden</strong>. El estado indicado abajo se aplicará a todos los registros del archivo.
                            </p>

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Estado a aplicar</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ej: 4"
                                        value={statusUpdateEstado}
                                        onChange={(e) => setStatusUpdateEstado(e.target.value)}
                                        disabled={statusUpdateProgress.isUploading}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Archivo Excel</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".xlsx,.xls"
                                        onChange={handleStatusUpdateFileUpload}
                                        disabled={statusUpdateProgress.isUploading}
                                    />
                                </div>
                            </div>
                            {statusUpdateFileName && <small className="text-muted mt-1 d-block mb-2">Archivo seleccionado: {statusUpdateFileName}</small>}

                            {statusUpdateCsvData.length > 0 && !statusUpdateProgress.isUploading && (
                                <div className="mb-3">
                                    <div className="alert alert-info">
                                        <strong>{statusUpdateCsvData.length}</strong> registros encontrados.
                                    </div>
                                    <div className="table-premium-wrapper" style={{ maxHeight: "200px", overflow: "auto" }}>
                                        <table className="table table-sm table-bordered" style={{ fontSize: "0.8rem" }}>
                                            <thead>
                                                <tr>
                                                    {Object.keys(statusUpdateCsvData[0] || {}).map(k => <th key={k}>{k}</th>)}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statusUpdateCsvData.slice(0, 5).map((row, i) => (
                                                    <tr key={i}>
                                                        {Object.values(row).map((val, j) => <td key={j}>{String(val)}</td>)}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {statusUpdateCsvData.length > 5 && <p className="text-center text-muted mt-2" style={{ fontSize: '0.8rem' }}>... y {statusUpdateCsvData.length - 5} más</p>}
                                </div>
                            )}

                            {statusUpdateProgress.isUploading && (
                                <div className="mb-3">
                                    <div className="alert alert-warning">
                                        <strong><span className="spinner-premium"></span> Enviando actualizaciones...</strong>
                                        <div className="mt-2">
                                            Registro {statusUpdateProgress.current} de {statusUpdateProgress.total}
                                            <span className="text-success ms-2">✓ {statusUpdateProgress.success}</span>
                                            <span className="text-danger ms-2">✗ {statusUpdateProgress.failed}</span>
                                        </div>
                                        <div className="progress mt-2" style={{ height: "25px" }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                role="progressbar"
                                                style={{ width: `${(statusUpdateProgress.current / statusUpdateProgress.total) * 100}%` }}
                                            >
                                                {Math.round((statusUpdateProgress.current / statusUpdateProgress.total) * 100)}%
                                            </div>
                                        </div>
                                        <small className="text-muted d-block mt-2">
                                            No cierre ni recargue la página mientras el proceso esté en curso.
                                        </small>
                                    </div>
                                </div>
                            )}

                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-success-premium"
                                    onClick={handleSendStatusUpdate}
                                    disabled={statusUpdateCsvData.length === 0 || statusUpdateProgress.isUploading}
                                >
                                    {statusUpdateProgress.isUploading
                                        ? <><span className="spinner-premium"></span>Enviando...</>
                                        : "📤 Enviar Actualizaciones"
                                    }
                                </button>

                                {statusUpdateProgress.isUploading && (
                                    <button
                                        className="btn btn-danger-premium"
                                        onClick={handleStopStatusUpdate}
                                    >
                                        ⏹ Detener
                                    </button>
                                )}
                            </div>

                            {statusUpdateResults.length > 0 && (
                                <div className="table-premium-wrapper mt-4" style={{ maxHeight: "350px", overflow: "auto" }}>
                                    <table className="table table-sm table-striped table-bordered" style={{ fontSize: "0.8rem" }}>
                                        <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                                            <tr>
                                                <th>#</th>
                                                <th>ID Orden</th>
                                                <th>Estado</th>
                                                <th>Resultado</th>
                                                <th>Respuesta</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {statusUpdateResults.map((r, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td>{r.idOrden}</td>
                                                    <td>{r.estado}</td>
                                                    <td>
                                                        {r.success === true && <span className="badge bg-success">✓ Enviado</span>}
                                                        {r.success === false && <span className="badge bg-danger">✗ Error</span>}
                                                        {r.success === null && <span className="badge bg-secondary">Cancelado</span>}
                                                    </td>
                                                    <td style={{ maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.message}>{r.message}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "bdReport" && (
                        <>
                            <div className="glass-card" style={{ padding: '1.5rem' }}>
                                <h5 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>🗄️ Descarga completa de la BD de Domicilios</h5>
                                <div className="alert alert-info" style={{ fontSize: '0.88rem' }}>
                                    <strong>¿Qué hace este módulo?</strong>
                                    <ul className="mb-0 mt-2" style={{ paddingLeft: '1.2rem' }}>
                                        <li>Descarga <strong>toda la información</strong> registrada en la base de datos de domicilios, tanto pedidos <strong>procesados</strong> como <strong>sin procesar</strong>, con <strong>todas sus columnas</strong>.</li>
                                        <li>El filtro se aplica sobre la <strong>Fecha de Envío</strong> del pedido (<code>shippingDateOrder</code>): se incluyen los pedidos cuya fecha de envío esté entre la fecha inicial y la final, ambas inclusive.</li>
                                        <li>Puede consultar hasta <strong>{BD_MAX_DIAS} días</strong>. Por el volumen de datos, el sistema consulta automáticamente en <strong>tramos de 7 días</strong> y en bloques de {BD_PAGE_SIZE.toLocaleString()} registros, y al final <strong>une todo en un solo archivo</strong>.</li>
                                        <li>Durante la descarga verá el avance de cada tramo. <strong>No cierre ni recargue la página</strong> hasta que termine; puede tardar varios minutos.</li>
                                    </ul>
                                </div>
                                <form onSubmit={handleBdReport}>
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-3">
                                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Fecha de envío inicial</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={bdDateIni}
                                                onChange={(e) => setBdDateIni(e.target.value)}
                                                disabled={bdProgreso.running}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Fecha de envío final</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={bdDateFin}
                                                onChange={(e) => setBdDateFin(e.target.value)}
                                                disabled={bdProgreso.running}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Formato</label>
                                            <select
                                                className="form-select"
                                                value={bdFormato}
                                                onChange={(e) => setBdFormato(e.target.value)}
                                                disabled={bdProgreso.running}
                                            >
                                                <option value="xlsx">Excel (.xlsx) — recomendado</option>
                                                <option value="csv">CSV (;) — para volúmenes muy grandes</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3 d-flex gap-2">
                                            <button className="btn btn-premium w-100" type="submit" disabled={bdProgreso.running}>
                                                {bdProgreso.running
                                                    ? <><span className="spinner-premium"></span>Descargando...</>
                                                    : "Consultar y Descargar"
                                                }
                                            </button>
                                            {bdProgreso.running && (
                                                <button type="button" className="btn btn-danger-premium" title="Detener descarga" aria-label="Detener descarga" onClick={() => { cancelBdRef.current = true; bdAbortRef.current?.abort(); }}>
                                                    ⏹
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <small className="text-muted mt-2 d-block fst-italic">
                                        Excel conserva los números de identificación y autorización como texto (sin perder ceros ni convertirlos a notación científica). Use CSV solo si el archivo Excel resulta demasiado pesado.
                                    </small>
                                </form>
                            </div>

                            {bdProgreso.tramos.length > 0 && (
                                <div className="glass-card" style={{ padding: '1.5rem', marginTop: '1rem' }}>
                                    <h6 style={{ fontWeight: 600, marginBottom: '0.75rem' }}>
                                        {bdProgreso.running
                                            ? <>Tramo {bdProgreso.tramoActual} de {bdProgreso.totalTramos} · bloque {bdProgreso.pagina} · <strong>{bdProgreso.registros.toLocaleString()}</strong> registros acumulados</>
                                            : <>Resultado: <strong>{bdProgreso.registros.toLocaleString()}</strong> registros</>
                                        }
                                    </h6>
                                    <div className="progress mb-3" style={{ height: "22px" }}>
                                        <div
                                            className={`progress-bar ${bdProgreso.running ? "progress-bar-striped progress-bar-animated" : ""}`}
                                            role="progressbar"
                                            style={{ width: `${(bdProgreso.tramos.filter(t => t.estado === "completado").length / bdProgreso.totalTramos) * 100}%` }}
                                        >
                                            {bdProgreso.tramos.filter(t => t.estado === "completado").length} / {bdProgreso.totalTramos} tramos
                                        </div>
                                    </div>
                                    <div className="table-premium-wrapper">
                                        <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                                            <thead className="table-dark">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Fecha de envío</th>
                                                    <th>Registros</th>
                                                    <th>Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bdProgreso.tramos.map((t, i) => (
                                                    <tr key={i}>
                                                        <td>{i + 1}</td>
                                                        <td>{bdDDMM(t.date)} – {bdDDMM(t.dateFinal)}</td>
                                                        <td>{t.registros.toLocaleString()}</td>
                                                        <td>
                                                            {t.estado === "pendiente" && <span className="badge bg-secondary">Pendiente</span>}
                                                            {t.estado === "consultando" && <span className="badge bg-warning text-dark">Consultando...</span>}
                                                            {t.estado === "completado" && <span className="badge bg-success">✓ Completado</span>}
                                                            {t.estado === "cancelado" && <span className="badge bg-secondary">Cancelado</span>}
                                                            {t.estado === "error" && <span className="badge bg-danger">✗ Error</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === "report" && (
                        <>
                            <div className="glass-card" style={{ padding: '1.5rem' }}>
                                <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>📊 Reporte General por Rango de Fechas</h5>
                                <form onSubmit={handleSearchByDate}>
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-4">
                                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Fecha Inicio</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={reportDate}
                                                onChange={(e) => setReportDate(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Fecha Final</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={reportDateFinal}
                                                onChange={(e) => setReportDateFinal(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <button
                                                className="btn btn-premium w-100"
                                                type="submit"
                                                disabled={loadingReport}
                                            >
                                                {loadingReport
                                                    ? <><span className="spinner-premium"></span>Generando...</>
                                                    : "Consultar y Descargar"
                                                }
                                            </button>
                                        </div>
                                    </div>
                                    <small className="text-muted mt-2 d-block fst-italic">
                                        El rango máximo de consulta es de 31 días (se consulta en tramos de 7 días y se descarga un solo Excel). Ambas fechas son obligatorias.
                                    </small>
                                </form>
                                {loadingReport && (
                                    <div className="alert alert-info mt-3">
                                        <span className="spinner-premium"></span> Generando archivo Excel, por favor espere...
                                    </div>
                                )}
                            </div>

                            <div className="glass-card" style={{ padding: '1.5rem', marginTop: '1rem' }}>
                                <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>📦 Reporte Pendientes por Entregar</h5>
                                <form onSubmit={handleSearchPendingReport}>
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-4">
                                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Fecha Inicio</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={reportPendingDate}
                                                onChange={(e) => setReportPendingDate(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Fecha Final</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={reportPendingDateFinal}
                                                onChange={(e) => setReportPendingDateFinal(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <button
                                                className="btn btn-success-premium w-100"
                                                type="submit"
                                                disabled={loadingPendingReport}
                                            >
                                                {loadingPendingReport
                                                    ? <><span className="spinner-premium"></span>Generando...</>
                                                    : "Consultar y Descargar"
                                                }
                                            </button>
                                        </div>
                                    </div>
                                    <small className="text-muted mt-2 d-block fst-italic">
                                        Reporte para consultar los pedidos que están pendientes por entregar (CHECKED_OUT), pero aún no se reportan en esta plataforma. El rango máximo de consulta es de 15 días.
                                    </small>
                                </form>
                                {loadingPendingReport && (
                                    <div className="alert alert-info mt-3">
                                        <span className="spinner-premium"></span> Generando archivo Excel de Pendientes, por favor espere...
                                    </div>
                                )}
                            </div>

                            <div className="glass-card" style={{ padding: '1.5rem', marginTop: '1rem' }}>
                                <h5 style={{ marginBottom: '1rem', fontWeight: 600 }}>📄 Informe con Resumen General</h5>
                                <form onSubmit={handleSearchSummaryReport}>
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-6">
                                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.85rem' }}>Fecha de Consulta</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={reportSummaryDate}
                                                onChange={(e) => setReportSummaryDate(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <button
                                                className="btn btn-accent w-100"
                                                type="submit"
                                                disabled={loadingSummaryReport}
                                            >
                                                {loadingSummaryReport
                                                    ? <><span className="spinner-premium"></span>Generando...</>
                                                    : "Consultar y Descargar"
                                                }
                                            </button>
                                        </div>
                                    </div>
                                    <small className="text-muted mt-2 d-block fst-italic">
                                        Descarga el informe Gerencial de ejecuciónes por fecha y los que fueron rechazados pro el sistema
                                    </small>
                                </form>
                                {loadingSummaryReport && (
                                    <div className="alert alert-warning mt-3">
                                        <span className="spinner-premium"></span> Consultando y descargando informe con resumen general, por favor espere...
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="alert alert-danger mt-3 fade-in">
                        {error}
                    </div>
                )}

                {/* Results Table - Status Log */}
                {resultados.length > 0 && activeTab === "statusLog" && (
                    <div className="table-premium-wrapper mt-4 fade-in">
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-striped table-bordered table-sm" style={{ fontSize: '0.8rem' }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>ID</th>
                                        <th>ID Orden</th>
                                        <th>Aut. Number</th>
                                        <th>Type Order</th>
                                        <th>ID Type</th>
                                        <th>Patient ID</th>
                                        <th>Status Order</th>
                                        <th>Status HQ</th>
                                        <th>New Status</th>
                                        <th>Observaciones</th>
                                        <th>Was Pending</th>
                                        <th>Status Date</th>
                                        <th>Fecha Mod.</th>
                                        <th>Headers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resultados.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.id}</td>
                                            <td>{item.idorden}</td>
                                            <td>{item.authorizationNumber}</td>
                                            <td>{item.typeOrder}</td>
                                            <td>{item.identificationType}</td>
                                            <td>{item.PatientidentificationPatient}</td>
                                            <td>{item.statusOrder}</td>
                                            <td>{item.statusOrderHeadquarters}</td>
                                            <td>{item.newStatus}</td>
                                            <td>{item.observaciones_nota}</td>
                                            <td>{item.wasPending ? "Si" : "No"}</td>
                                            <td>{item.statusDate}</td>
                                            <td>{item.fecha_modificacion}</td>
                                            <td>
                                                <button
                                                    className="btn btn-info btn-sm"
                                                    onClick={() => handleShowHeaders(item.request_headers)}
                                                    title="Ver Headers"
                                                    style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}
                                                >
                                                    Ver
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Results Table - General */}
                {resultados.length > 0 && activeTab !== "statusLog" && (
                    <div className="table-premium-wrapper mt-4 fade-in">
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-striped table-bordered table-sm">
                                <thead className="table-dark">
                                    <tr>
                                        <th>Estado Orden Global</th>
                                        <th>ID</th>
                                        <th>Identificación</th>
                                        <th>Tipo ID</th>
                                        <th>Orden</th>
                                        <th>Operador</th>
                                        <th>Estado orden</th>
                                        <th>Cumple Entrega</th>
                                        <th>Tiempo Despacho / Entrega</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resultados.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                {item.statusOrder}
                                                {index === 0 && allItemsReadyForBulkAction() && (
                                                    <button
                                                        className="btn btn-info btn-sm ms-2"
                                                        onClick={handleBulkAction}
                                                        title="Ejecutar acción masiva"
                                                        style={{ fontSize: '0.72rem', padding: '0.15rem 0.45rem' }}
                                                    >
                                                        ✓
                                                    </button>
                                                )}
                                            </td>
                                            <td>{item.idorden}</td>
                                            <td>{item.identificationPatient}</td>
                                            <td>{item.identificationTypePatient}</td>
                                            <td>{item.authorizationNumber}</td>
                                            <td>{item.nameProvaider}</td>
                                            <td>{item.statusOrderHeadquarters}</td>
                                            <td style={{
                                                color: calcularCumpleEntrega(item.shippingDateOrder, item.date_notificacion, item.statusOrder, item.statusOrderHeadquarters).color,
                                                fontWeight: 'bold'
                                            }}>
                                                {calcularCumpleEntrega(item.shippingDateOrder, item.date_notificacion, item.statusOrder, item.statusOrderHeadquarters).text}
                                            </td>
                                            <td style={{
                                                backgroundColor: calcularTiempoEntrega(item.packingDate, item.shippingDateOrder, item.statusOrder).color,
                                                color: 'white',
                                                fontWeight: 'bold',
                                                textAlign: 'center'
                                            }}>
                                                {calcularTiempoEntrega(item.packingDate, item.shippingDateOrder, item.generationDateOrder).text}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    <span
                                                        className="badge bg-primary"
                                                        onClick={() => handleShowDetails(item)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        Ver más
                                                    </span>

                                                    {item.statusOrderHeadquarters === "CHECKED_OUT" && (
                                                        <>
                                                            <span
                                                                className="badge bg-success"
                                                                onClick={() => handleEntregado(item)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                Entregado
                                                            </span>

                                                            <span
                                                                className="badge bg-danger"
                                                                onClick={() => handleDeclinar(item)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                DEVOLUCIÓN
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>

            {/* Modal de detalle */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>📋 Detalle del Registro</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detalle && (
                        <div>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <tbody>
                                        {Object.entries(detalle).map(([key, value]) => {
                                            if (key === "json_notificacion" || key === "payloadParsed") return null;

                                            return (
                                                <tr key={key}>
                                                    <th className="bg-light" style={{ width: "30%" }}>{key}</th>
                                                    <td>{value === null || value === "" ? <span className="text-muted">N/A</span> : String(value)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {detalle.json_notificacion && (
                                <>
                                    <h5 className="mt-3">Notificación (JSON):</h5>
                                    <pre className="bg-light p-3 rounded" style={{ maxHeight: "200px", overflow: "auto" }}>
                                        {JSON.stringify(detalle.json_notificacion, null, 2)}
                                    </pre>
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn btn-glass" onClick={handleCloseModal}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para Request Headers (Status Log) */}
            <Modal show={logHeadersModal} onHide={handleCloseHeadersModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>🔗 Request Headers</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedHeaders ? (
                        <pre className="bg-light p-3 rounded" style={{ maxHeight: "400px", overflow: "auto" }}>
                            {JSON.stringify(selectedHeaders, null, 2)}
                        </pre>
                    ) : (
                        <p>No headers content.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn btn-glass" onClick={handleCloseHeadersModal}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default OrderManager;
